import {
  cellFraction,
  matrixRows,
} from '@components/AccountAllocationGrid/placeholderMatrix';
import type { AccountAllocationGridReport } from '@components/AccountAllocationGrid/types';
import { allocationCellKey } from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import { CUSTOM_OVERRIDE_TOLERANCE } from '@ovComponents/4-module/widgets/portfolio/components/allocationTolerances';
import { isAssetClassAllocationModel } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { evaluateAllocationViolations } from '@ovComponents/4-module/widgets/portfolio/components/computeAllocationHighlights';
import { tradeableAllocationSubAccounts } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import {
  amountCentsToFraction,
  fractionToAmountCents,
} from '@utils/allocationUnits';
import {
  accountAllocationFingerprint,
  type StagedAccountAllocations,
  stagedMembershipChange,
} from './accountAllocationStaging';
import type { StagedAccountAllocationsState } from './hooks/useStagedAccountAllocations';

export type ReviewBlockCode =
  | 'MISSING_STAGING'
  | 'STALE_REPORT'
  | 'MEMBERSHIP_CHANGED'
  | 'BASELINE_CHANGED'
  | 'INVALID_ACCOUNT_TOTALS';

// null on either side means the account doesn't hold the fund.
export type AccountAllocationComparisonCell = {
  subAccountId: string;
  financialProductId: string;
  current: number | null;
  proposed: number | null;
  changed: boolean;
};

export type AccountAllocationAccountTotal = {
  subAccountId: string;
  proposed: number;
  current: number;
  valid: boolean;
};

export type AccountAllocationFundTotal = {
  financialProductId: string;
  proposed: number;
  target: number;
  withinTolerance: boolean;
};

export type AccountAllocationComparison = {
  cells: AccountAllocationComparisonCell[];
  accountTotals: AccountAllocationAccountTotal[];
  fundTotals: AccountAllocationFundTotal[];
  changedCount: number;
};

export type ReviewDecision =
  | { status: 'REVIEWABLE'; comparison: AccountAllocationComparison }
  | {
      status: 'BLOCKED';
      code: ReviewBlockCode;
      added?: string[];
      removed?: string[];
    };

type ProposedCell = { sa: string; fp: string; fraction: number };

const blocked = (code: ReviewBlockCode): ReviewDecision => ({
  status: 'BLOCKED',
  code,
});

const comparisonCells = (
  { matrix }: StagedAccountAllocations,
  report: AccountAllocationGridReport,
) => {
  const isAssetClass = isAssetClassAllocationModel(report);
  const accounts = new Map(
    report.subAccounts.map((account) => [account.subAccount.id, account]),
  );
  const rows = new Map(
    matrixRows(report).map((row) => [row.financialProduct.id, row]),
  );

  return matrix.subAccountIds.flatMap((subAccountId, row) => {
    const account = accounts.get(subAccountId);
    return matrix.financialProductIds.map(
      (financialProductId, column): AccountAllocationComparisonCell => {
        const proposed = matrix.fractions[row][column];
        const fundRow = rows.get(financialProductId);
        const current =
          (account &&
            fundRow &&
            cellFraction(account, fundRow, isAssetClass)) ??
          null;
        return {
          subAccountId,
          financialProductId,
          current,
          proposed,
          changed:
            proposed !== null &&
            (current === null ||
              Math.abs(proposed - current) > CUSTOM_OVERRIDE_TOLERANCE),
        };
      },
    );
  });
};

const proposedCells = (cells: AccountAllocationComparisonCell[]) =>
  cells.flatMap(({ subAccountId, financialProductId, proposed }) =>
    proposed === null
      ? []
      : [{ sa: subAccountId, fp: financialProductId, fraction: proposed }],
  );

const accountTotals = ({
  subAccountIds,
  cells,
  proposedCells,
  invalidCells,
  cashFraction,
}: {
  subAccountIds: string[];
  cells: AccountAllocationComparisonCell[];
  proposedCells: ProposedCell[];
  invalidCells: Set<string>;
  cashFraction: number;
}) =>
  subAccountIds.map((subAccountId) => {
    const accountCells = proposedCells.filter(({ sa }) => sa === subAccountId);
    return {
      subAccountId,
      proposed: accountCells.reduce(
        (total, { fraction }) => total + fraction,
        cashFraction,
      ),
      current: cells
        .filter((cell) => cell.subAccountId === subAccountId)
        .reduce((total, { current }) => total + (current ?? 0), cashFraction),
      // The solver's check marks cells, so an account staged with none would
      // pass it while holding only cash. Continue never stages one; fail closed.
      valid:
        accountCells.length > 0 &&
        !accountCells.some(({ sa, fp }) =>
          invalidCells.has(allocationCellKey(sa, fp)),
        ),
    };
  });

const fundTotals = ({
  financialProductIds,
  cells,
  members,
  rowTargetFraction,
  driftedCells,
}: {
  financialProductIds: string[];
  cells: ProposedCell[];
  members: { sa: string; mvCents: number }[];
  rowTargetFraction: Map<string, number>;
  driftedCells: Set<string>;
}) => {
  const mvCentsOf = new Map(members.map(({ sa, mvCents }) => [sa, mvCents]));
  const householdCents = members.reduce(
    (total, { mvCents }) => total + mvCents,
    0,
  );

  return financialProductIds.map((financialProductId) => {
    const rowCells = cells.filter(({ fp }) => fp === financialProductId);
    const rowCents = rowCells.reduce(
      (total, { sa, fraction }) =>
        total + fractionToAmountCents(fraction, mvCentsOf.get(sa) ?? 0),
      0,
    );
    return {
      financialProductId,
      proposed: amountCentsToFraction(rowCents, householdCents),
      target: rowTargetFraction.get(financialProductId) ?? 0,
      withinTolerance: !rowCells.some(({ sa, fp }) =>
        driftedCells.has(allocationCellKey(sa, fp)),
      ),
    };
  });
};

// Account totals block and fund totals don't (D1): fund weights follow market
// values, which move between Recalculate and review without any edit, and
// signed percentages apply as signed. So the solver's check runs twice: with
// no row targets it reports only accounts that don't add up, and once every
// account does, a second run can only report fund drift.
const compareStagedAllocations = (
  staged: StagedAccountAllocations,
  report: AccountAllocationGridReport,
): ReviewDecision => {
  const cells = comparisonCells(staged, report);
  const proposed = proposedCells(cells);
  const members = tradeableAllocationSubAccounts(report.subAccounts).map(
    (account) => ({
      sa: account.subAccount.id,
      mvCents: account.marketValueCents,
    }),
  );
  const rowTargetFraction = new Map(
    matrixRows(report).map((row) => [
      row.financialProduct.id,
      row.expectedPercentage,
    ]),
  );
  const securityMassFraction = 1 - report.expectedCashPercentage;

  const { violationCells: invalidCells } = evaluateAllocationViolations({
    members,
    rowTargetFraction: new Map(),
    cells: proposed,
    securityMassFraction,
  });
  const totals = accountTotals({
    subAccountIds: staged.matrix.subAccountIds,
    cells,
    proposedCells: proposed,
    invalidCells,
    cashFraction: report.expectedCashPercentage,
  });
  if (totals.some(({ valid }) => !valid)) {
    return blocked('INVALID_ACCOUNT_TOTALS');
  }

  const { violationCells: driftedCells } = evaluateAllocationViolations({
    members,
    rowTargetFraction,
    cells: proposed,
    securityMassFraction,
  });

  return {
    status: 'REVIEWABLE',
    comparison: {
      cells,
      accountTotals: totals,
      fundTotals: fundTotals({
        financialProductIds: staged.matrix.financialProductIds,
        cells: proposed,
        members,
        rowTargetFraction,
        driftedCells,
      }),
      changedCount: cells.filter(({ changed }) => changed).length,
    },
  };
};

// The review shows exactly what the apply step will write: the staged matrix.
// Order matters: membership before the baseline, so an added account is named.
export const decideAccountAllocationReview = (
  state: Exclude<StagedAccountAllocationsState, { status: 'LOADING' }>,
  report: AccountAllocationGridReport,
): ReviewDecision => {
  // 'in', not status: ABSENT shares a union member with LOADING, so a status
  // check alone can't narrow to a staging. Anything without one fails closed.
  if (!('staged' in state) || !state.baseline) {
    return blocked('MISSING_STAGING');
  }
  const { status, staged, baseline } = state;
  if (status === 'STALE_REPORT') return blocked('STALE_REPORT');

  const membership = stagedMembershipChange(staged, report);
  if (membership) {
    return { status: 'BLOCKED', code: 'MEMBERSHIP_CHANGED', ...membership };
  }
  if (accountAllocationFingerprint(report) !== baseline.fingerprint) {
    return blocked('BASELINE_CHANGED');
  }

  return compareStagedAllocations(staged, report);
};
